import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { 
  EyeIcon, 
  EyeSlashIcon, 
  UserIcon, 
  LockClosedIcon,
  UserGroupIcon,
  CalculatorIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

import { 
  loginStart, 
  loginSuccess, 
  loginFailure,
  selectIsAuthenticated,
  selectAuthLoading 
} from '../../store/slices/authSlice';
import { authService, LoginCredentials } from '../../services/authService';

interface LoginFormData {
  email: string;
  password: string;
  remember?: boolean;
}

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectAuthLoading);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>();

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    try {
      dispatch(loginStart());

      const credentials: LoginCredentials = {
        email: data.email,
        password: data.password,
      };

      const response = await authService.login(credentials);

      dispatch(loginSuccess({
        user: response.data.user,
        token: response.data.token,
        permissions: response.data.permissions,
        roles: response.data.roles,
      }));

      toast.success('Login successful! Welcome back.');
      navigate('/dashboard');
    } catch (error: any) {
      const errorMessage = error.message || 'Login failed. Please check your credentials.';
      dispatch(loginFailure(errorMessage));
      toast.error(errorMessage);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col md:flex-row font-sans">
      
      {/* Left Column - Marketing & Branding */}
      <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-cyan-600 to-cyan-800 text-white p-12 flex-col justify-between relative overflow-hidden">
        {/* Decorative background circles */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full -translate-x-1/2 -translate-y-1/2 blur-2xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400 opacity-20 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl"></div>
        
        <div className="relative z-10">
          <div className="mb-12"></div>
          
          <h1 className="text-4xl lg:text-5xl font-extrabold leading-tight mb-6">
            Selamat Datang <br/>
            di smart system
            <div className="flex w-full justify-center mt-6">
              <div className="inline-flex items-center justify-center overflow-hidden" style={{ width: '180px', height: '60px' }}>
                <img src="/Logo macLi.png" alt="macLi" className="w-full h-full object-contain transform scale-[2.2] brightness-0 invert" />
              </div>
            </div>
          </h1>
          <p className="text-cyan-100 text-lg mb-10 max-w-md leading-relaxed text-justify">
            Sistem ERP Klinik terintegrasi penuh. Mulai dari absen wajah otomatis, HRD, operasional medis, analisa revenue (auto analisa sistem report), hingga pajak dalam satu platform.
          </p>
          
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="bg-cyan-500/30 p-3 rounded-lg"><UserGroupIcon className="w-6 h-6 text-cyan-200"/></div>
              <div>
                <h3 className="font-semibold text-lg">Portal HR & Absen Wajah</h3>
                <p className="text-cyan-200 text-sm">Sistem presensi biometrik berakurasi tinggi dan portal karyawan mandiri.</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-cyan-500/30 p-3 rounded-lg"><CalculatorIcon className="w-6 h-6 text-cyan-200"/></div>
              <div>
                <h3 className="font-semibold text-lg">Finance, Pajak & Marketing</h3>
                <p className="text-cyan-200 text-sm">Otomatisasi laporan keuangan, kalkulator PPN, PPh, & analitik target.</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-cyan-500/30 p-3 rounded-lg"><BuildingOfficeIcon className="w-6 h-6 text-cyan-200"/></div>
              <div>
                <h3 className="font-semibold text-lg">Manajemen Klinik Terpadu</h3>
                <p className="text-cyan-200 text-sm">Pendaftaran, antrean, poli, kasir, apotek, hingga stok inventori.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-sm text-cyan-200/60 mt-12">
          © {new Date().getFullYear()} macLi. All rights reserved.
        </div>
      </div>

      {/* Right Column - Login Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-gray-50 relative">
        {/* Mobile Header (Only visible on small screens) */}
        <div className="md:hidden absolute top-8 left-8 flex items-center">
           <div className="overflow-hidden flex items-center justify-center" style={{ width: '120px', height: '48px' }}>
             <img src="/Logo macLi.png" alt="macLi Logo" className="w-full h-full object-contain transform scale-[1.8]" />
           </div>
        </div>

        {/* Top Right Logo (Visible on all screens, or mainly desktop) */}
        <div className="absolute top-8 right-8 flex items-center hidden md:flex">
           <div className="overflow-hidden flex items-center justify-center" style={{ width: '120px', height: '48px' }}>
             <img src="/Logo macLi.png" alt="macLi Logo" className="w-full h-full object-contain transform scale-[1.8]" />
           </div>
        </div>

        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Sign In</h2>
            <p className="text-gray-500 text-sm">Silakan masukkan kredensial Anda untuk masuk ke sistem.</p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Please enter a valid email address'
                    }
                  })}
                  type="email"
                  autoComplete="email"
                  className={`block w-full pl-10 pr-3 py-3 rounded-xl bg-gray-50 border shadow-sm text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-colors ${
                    errors.email ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-cyan-500'
                  }`}
                  placeholder="adm@klinik.com"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className={`block w-full pl-10 pr-10 py-3 rounded-xl bg-gray-50 border shadow-sm text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-colors ${
                    errors.password ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-cyan-500'
                  }`}
                  placeholder="Enter your password"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-cyan-500 focus:outline-none transition-colors"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {/* Remember Me */}
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center">
                <input
                  {...register('remember')}
                  id="remember"
                  type="checkbox"
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded cursor-pointer"
                />
                <label htmlFor="remember" className="ml-2 block text-sm text-gray-700 cursor-pointer select-none">
                  Remember me
                </label>
              </div>
              <div className="text-sm">
                <a href="#" className="font-medium text-cyan-600 hover:text-cyan-500 transition-colors">
                  Forgot your password?
                </a>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting || isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-md text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {(isSubmitting || isLoading) ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Signing in...
                  </span>
                ) : 'Sign in'}
              </button>
            </div>
            
            {/* Registration Link */}
            <div className="text-center mt-6 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                Belum mendaftarkan klinik Anda?{' '}
                <button 
                  type="button" 
                  onClick={() => navigate('/daftar-klinik')} 
                  className="font-semibold text-cyan-600 hover:text-cyan-800 transition-colors"
                >
                  Daftar Klinik Baru
                </button>
              </p>
            </div>
          </form>


        </div>
      </div>
    </div>
  );
};

export default LoginPage;